import React, { useCallback, useState, useEffect } from 'react';
import { Box, Flex, Image, Text, Button } from 'rebass';
import PropTypes from 'prop-types';
import useSWR from 'swr';
import { MdOutlineFileUpload } from "react-icons/md";
import { useDropzone } from 'react-dropzone';
import { GoFileMedia } from "react-icons/go";
import { IoCrop } from "react-icons/io5";
import { Spinner } from 'app/loading/styled';
import { createMediaForWatchMeGrow } from 'story-media/actions';
import useEntity from 'entities/hooks/use-entity';
import { MediaUploadSection } from './VideoRenderer';
import { VideoUploadLabel } from 'story-media/new/big-selfie-button';
import Modal from 'app/modal/v3/index';
import Editor from './ImageEditor/Editor';
import { getSWR, patch } from 'utils/api';
import { extensionRegex, supportedImageTypes } from 'stories/tell-a-story/FileDropperField';
import InvalidFileError from './InvalidFileError';
import { destroyStoryMedium } from 'story-media/api';
import FirstTimeUserExperience from '../FirstTimeUserExperience';
import store from 'store';
import { renderPositionForVGBImage, scalePosition } from './ImageEditor/helper';
import ReactAvatarEditor from 'react-avatar-editor';
import { getPillar } from 'stories/helper';
import { capCanvasDims } from 'collections/book-creation/helper';
import { getBestImageVersion } from 'utils/image-versions';

const ImageRenderer = ({ image, monthId, revalidate, mediaFtue, setMediaFtue, age, firstBookId }) => {

  const { data, revalidate: revalidateLayers } = useSWR(firstBookId ? `/v3/books/${firstBookId}/watch_me_grow_months/${monthId}/watch_me_grow_month_frame_layers` : null);
  const layersData = data?.data?.data || [];
  const monthsPhotoDetailsLayer = layersData.find(layer => layer.attributes['component-variable'] === 'month_photo');
  const framleLayer = layersData.find(layer => layer.attributes['component-value'] === 'frame');

  const { data: monthData, revalidate: revalidateMonth } = useSWR(firstBookId ? `/v3/books/${firstBookId}/watch_me_grow_months/${monthId}` : null);

  const [isLoading, setIsLoading] = useState(false);
  const [initialFiles, setInitialFiles] = useState(false);
  const [uploadingMedia, setMediaUpload] = useState(false);
  const [cropVideo, setCropVideo] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(0);
  const [scale, setScale] = useState(0);
  const [thumbnailDims, setThumbnailDims] = useState(null);
  const [modalDims, setModalDims] = useState(null);

  const [photoScaling, setPhotoScalling] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [showMediaFtue, setShowMediaFtue] = useState(null);
  const [newImageObj, setNewImageObj] = useState(null);
  const [updatedMediaId, setUpdatedMediaId] = useState(null);
  // Object URL of the just-cropped local image, shown instead of the spinner while the
  // upload processes. Cleared when the server image finishes; lost on refresh (then the
  // processing loader takes over).
  const [localPreview, setLocalPreview] = useState(null);

  const { entity: clipDetails } = useEntity(image);
  const { entity: mediaDetailsClips } = useEntity(clipDetails?.image)

  // Canvas dimensions for the preview/thumbnail avatar editors. Use the ORIGINAL clip
  // dimensions (capped to a mobile-safe canvas size, which preserves aspect ratio). This
  // must match the interactive Editor (mediaAspectRatio) and ImageNode so the same crop
  // params render identically on every device — a device-specific image variant has a
  // slightly different aspect ratio and would drift the crop on mobile.
  const editorDims = capCanvasDims(mediaDetailsClips?.width, mediaDetailsClips?.height);
  const { entity: framleLayerDetails } = useEntity(framleLayer?.['relationships']?.['photo']?.data)
  const photoData = monthsPhotoDetailsLayer?.['relationships']?.['photo']?.data
  const mediaDetails = monthData?.data?.included.find(({ type }) => type === 'images')?.attributes
  const { data: mainData, revalidate: revalidateMedia } = useSWR((updatedMediaId || mediaDetails?.id) ? `/v2/media/${updatedMediaId || mediaDetails?.id}` : null, getSWR, { refreshInterval });

  let lockVersion = mediaDetails?.['lock-version']

  const [dropError, setDropError] = useState([]);

  let photoAttributes = mediaDetails

  const polledAttrs = mainData?.data?.data?.attributes;
  const mediaAttrs = polledAttrs || mediaDetails;
  const uploadStatus = polledAttrs?.state || mediaDetails?.state;
  const mediaAssetUrl = getBestImageVersion(mediaAttrs).url

  const frameSrc = getBestImageVersion(framleLayerDetails).url

  const handleChangeImageUrl = async (url) => {
    if (!url) return; // guard: a falsy url would throw on .startsWith and leave a black tile
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      setNewImageObj(url);
      return;
    }
    try {
      const blob = await imageUrlToBlob(url);
      setNewImageObj(URL.createObjectURL(blob));
    } catch (err) {
      setNewImageObj(url);
    }
  };
  useEffect(() => {
    if (mediaAssetUrl) {
      setImageUrl(mediaAssetUrl)
      handleChangeImageUrl(mediaAssetUrl)
    }
  }, [mediaAssetUrl]);

  useEffect(() => {
    if (
      uploadStatus === 'pending_upload' ||
      uploadStatus === 'uploaded' ||
      uploadStatus === 'processing'
    ) {
      setIsLoading(true);
      revalidateMonth();
      revalidateLayers();
      setRefreshInterval(3000);
    }

    if (uploadStatus === 'finished') {
      handleChangeImageUrl(getBestImageVersion(mediaAttrs).url)

      setRefreshInterval(0);
      setIsLoading(false);
      revalidateMonth()
      revalidateLayers();
      // NOTE: localPreview is intentionally NOT cleared here — it stays as an overlay
      // bridging the async gap until the real image finishes loading in the editor
      // (cleared in the thumbnail's onLoadSuccess), so there's no black flash.
    }
  }, [uploadStatus, setRefreshInterval]);

  useEffect(() => () => setLocalPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; }), [])

  useEffect(() => {
    if (photoAttributes) {
      if (photoAttributes?.width && photoAttributes?.left !== null && photoAttributes?.top !== null)
        setPhotoScalling({
          x: photoAttributes?.left,
          y: photoAttributes?.top,
        });
      setScale(photoAttributes?.zoom || 1);


    }
  }, [photoAttributes]);

  const submitMedia = async (val) => {

    const { croppedPostion, scale: newScaleValue, position, imageInfo, croppedBlob } = val;

    // Re-cropping an existing image happens instantly; a brand-new upload has to be
    // processed server-side first (the uploadStatus effect polls and clears the spinner).
    const isNewUpload = !cropVideo && !!initialFiles;

    // Show the just-cropped image locally (matches the crop the user set) while the
    // upload processes, instead of a bare spinner.
    if (isNewUpload) {
      try {
        const localBlob = croppedBlob || (initialFiles && initialFiles[0]);
        if (localBlob) {
          setLocalPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(localBlob); });
        }
      } catch (e) { /* noop */ }
    }

    let mediaId = photoData?.id
    setIsLoading(true);
    try {
      if (mediaFtue || !store.get('wmg_ftui_watched_media'))
        setShowMediaFtue(true)

      if (isNewUpload) {
        // The `image_upload_started` / `image_part_uploaded` posts that used to sit
        // here are gone: uploadMedium emits the whole lifecycle now, keyed on the
        // real medium id.
        //
        // They were also mislabelled. `image?.id` is the WatchMeGrowImageClip, not
        // the Medium, so every event posted from here recorded
        // 'subject-type': 'Medium' against a clip id -- pointing at a medium that
        // does not exist. That is why this path's telemetry coverage looked so odd.
        const resp = await createMediaForWatchMeGrow(monthId, initialFiles, false, {
          'owner-type': 'Books::WatchMeGrowImageClip',
          'owner-id': image?.id,
          ...imageInfo
        });
        if (resp[0] && resp[0]?.success) {
          const { id: textCanvasClipId, 'lock-version': updatedLockVeriosn } = resp[0].createdMedium;
          mediaId = textCanvasClipId
          lockVersion = updatedLockVeriosn
          // `image_upload_completed` removed: uploadMedium emits it when the PUT
          // actually returns, rather than after the create round-trip resolves.
        }
      }

      if (mediaId) {
        setUpdatedMediaId(mediaId)

        await patch(`/v2/media/${mediaId}`, {
          data: {
            attributes: {
              'lock-version': lockVersion,
              zoom: +newScaleValue,
              'crop-left': `${croppedPostion?.x}`,
              'crop-top': `${croppedPostion?.y}`,
              left: position?.x,
              top: position?.y,
              'crop-width': `${croppedPostion.width}%`,
              'crop-height': `${croppedPostion.height}%`,
            }
          }
        })
      }

      // Refetch AFTER the crop is saved so the rendered image / crop params / aspect
      // ratio update in place. Previously revalidate() ran BEFORE the PATCH, so it
      // pulled stale pre-crop data and the UI reverted to the old crop until a manual
      // page refresh. These normalized fetches update the entity the preview reads from
      // (mediaDetailsClips); revalidateMedia refreshes the upload-status poll.
      await Promise.all([
        revalidate && revalidate(),
        revalidateMonth(),
        revalidateLayers(),
        revalidateMedia(),
      ]);

      setInitialFiles(false);
      setMediaUpload(false);
      setCropVideo(false);
      // New uploads keep the spinner until server processing finishes (handled by the
      // uploadStatus effect); an existing-image re-crop is already done here.
      if (!isNewUpload) setIsLoading(false);
    }
    catch (error) {
      // No tracked event here any more. uploadMedium reports upload failures
      // itself, against the right subject id; this catch also wraps the crop PATCH
      // and the revalidations, so posting `image_upload_error_occurred` from it
      // recorded lock-version conflicts and refetch failures as upload errors.
      //
      // It also did `context: { ...error }`, which spreads an axios error's
      // `config` -- and that carries the Authorization header. Live session tokens
      // are sitting in events.context in production because of this line.
      console.error("Error uploading media:", error);
      onClose()
    }
  }

  const onClose = () => {
    if (cropVideo) {
      setCropVideo(false);
    } else {
      setMediaUpload(false)
      setCropVideo(false);
      setInitialFiles(false);
      setIsLoading(false);

    }
  }

  const handleChange = useCallback(
    (e, transcribe = false) => {
      setDropError([]);
      let filteredResp = e.filter((f) => {
        const fileExtensions = extensionRegex.exec(f.name.toLowerCase());

        return (
          f.type.includes('image') && supportedImageTypes.includes(fileExtensions[0].toLowerCase()))
      });

      if (filteredResp.length === e.length) {
        setInitialFiles(e)
        setScale(1)
        setPhotoScalling(null)
      } else {
        const b = new Set(filteredResp);
        const differenceOfArray = [...e].filter((x) => !b.has(x));
        setDropError(differenceOfArray);
      }
    },
    []
  );

  const { getRootProps, getInputProps } = useDropzone({ onDrop: (e) => handleChange(e, true) })

  if (isLoading) {
    return (
      <Box width={["122px", "153px"]} height={["149.661px", "188.128px"]}
        sx={{ borderRadius: 12, border: '1px dashed #2D9CDB', overflow: 'hidden', position: 'relative', bg: '#F3FCFF' }}
      >
        {localPreview ? (
          <>
            <Image src={localPreview} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            {/* subtle "still processing" indicator over the local preview */}
            <Flex sx={{
              position: 'absolute', bottom: '6px', right: '6px', bg: 'rgba(0,0,0,0.45)',
              borderRadius: '50%', p: '5px', alignItems: 'center', justifyContent: 'center',
            }}>
              <Spinner color="#fff" height={"16px"} width={"16px"} />
            </Flex>
          </>
        ) : (
          <Flex width="100%" height="100%" justifyContent="center" alignItems="center" color="#64748B">
            <Spinner color="#9B59B6" height={"44px"} width={"44px"} />
          </Flex>
        )}
      </Box>
    );
  }

  const doesMediaExist = !!mediaAssetUrl

  return <Box>
    {showMediaFtue && <FirstTimeUserExperience isForMedia setShowFTUE={() => {
      setShowMediaFtue(false)
      setMediaFtue(false)
      store.set('wmg_ftui_watched_media', true)
    }} />}

    {uploadingMedia && <Modal
      maxWidth={initialFiles ? 700 : 580}
      shadeProps={{ pt: [0, 4] }}
      closeButton
      close={{ onClose }}
      childSx={{ marginTop: '-40px', zIndex: 1 }}
      // aboveTooltip
      closeOnOutsideClick
    >
      <InvalidFileError
        errorFiles={dropError}
        onDismiss={() => setDropError([])}
        supportedTypes={['image']}
      />
      {(initialFiles || cropVideo) ? <Flex justifyContent="center" flexDirection="column" alignItems="center" p={4} pt={0}>
        <Text
          color="#000"
          fontFamily="Poppins"
          fontSize={"22px"}
          lineHeight="165%"
          mb={3}
        >
          Crop Photo
        </Text>
        <Text fontFamily="Poppins" fontSize={16} lineHeight="165%" color="#64748B" mb={3}>
          Photo Preview
        </Text>
        <Box
          width={["179.01px", "261.88px"]}
          height={["219.63px", "321.28px"]}
          ref={(el) => {
            if (!el) return;
            const { width, height } = el.getBoundingClientRect();
            // const frameAR = framleLayerDetails?.height && framleLayerDetails?.width
            //   ? framleLayerDetails.height / framleLayerDetails.width
            //   : mediaDetailsClips?.height && mediaDetailsClips?.width
            //     ? mediaDetailsClips.height / mediaDetailsClips.width
            //     : null;
            // const height = frameAR ? Math.round(width * frameAR) : el.getBoundingClientRect().height;
            setModalDims(prev => (prev?.width === width && prev?.height === height ? prev : { width, height }));
          }}
          sx={{ position: 'absolute', zIndex: -10 }}
        />
        <Editor
          frameSrc={frameSrc}
          posterAttributes={{
            position: photoScaling,
            scale,
            aspectRatio: { width: photoAttributes?.width, height: photoAttributes?.height },
            // monthsPhotoDetailsLayer?.attributes
          }}
          imageWidthInEditor={modalDims?.width}
          imageHeightInEditor={modalDims?.height}
          mediaAspectRatio={{
            height: mediaDetailsClips.height,
            width: mediaDetailsClips.width
          }}
          image={initialFiles ? initialFiles[0] : mediaAssetUrl}
          onAccept={submitMedia}
          // setSubmitting={setIsLoading}
          onClear={async () => {
            setIsLoading(true);
            setInitialFiles(false);
            setCropVideo(false);

            await Promise.all(
              [mediaDetailsClips].map(({ id }) => {
                return destroyStoryMedium(id)
              })
            );
            setMediaUpload(false)
            setImageUrl(null)
            revalidateMonth();
            revalidateLayers();
            setIsLoading(false);
          }}
          onClose={() => {
            setInitialFiles(false);
            // setCropVideo(false);
            // setIsLoading(false);
          }}
        />

      </Flex>
        : doesMediaExist ?
          <Flex justifyContent="center" flexDirection="column" alignItems="center" p={4} pt={0}>
            <Text
              color="#000"
              fontFamily="Poppins"
              fontSize={"22px"}
              lineHeight="165%"
              mb={3}
            >
              {age}
            </Text>

            <Text fontFamily="Poppins" fontSize={16} lineHeight="165%" color="#64748B" mb={3}>
              Photo Preview
            </Text>
            <Box
              width={["179.01px", "261.88px"]}
              height={["219.63px", "321.28px"]}
              ref={(el) => {
                if (!el) return;
                const { width, height } = el.getBoundingClientRect();
                // const frameAR = framleLayerDetails?.height && framleLayerDetails?.width
                //   ? framleLayerDetails.height / framleLayerDetails.width
                //   : mediaDetailsClips?.height && mediaDetailsClips?.width
                //     ? mediaDetailsClips.height / mediaDetailsClips.width
                //     : null;
                // const height = frameAR ? Math.round(width * frameAR) : el.getBoundingClientRect().height;
                setModalDims(prev => (prev?.width === width && prev?.height === height ? prev : { width, height }));
              }}
              sx={{ position: 'relative', borderRadius: '11.559px', overflow: 'hidden' }}
            >
              <ReactAvatarEditor
                image={newImageObj}
                height={editorDims.height}
                width={editorDims.width}
                color={[250, 250, 250, 0]}
                scale={mediaDetailsClips?.zoom === 0 ? 1 : (+scalePosition(mediaDetailsClips?.zoom) * 3) / 100}
                position={renderPositionForVGBImage({
                  x: mediaDetailsClips?.left,
                  y: mediaDetailsClips?.top,
                })}
                id={` YEH DAIKHO ${newImageObj}`}
                crossOrigin="anonymous"
                style={{
                  minWidth: "100%",
                  width: "100%",
                  height: "100%",
                  objectFit: 'cover',
                }}
              />
              {frameSrc && <Image
                width={(modalDims?.width || 0) + (getPillar(modalDims?.width || 0) * 2)}
                maxWidth='inherit'
                src={frameSrc}
                sx={{
                  position: 'absolute',
                  // Match the dashboard thumbnail on every device: shift the frame up by
                  // getPillar so its window aligns with the photo. Previously 'unset' on
                  // mobile, which mis-positioned the overlay vs desktop/dashboard.
                  top: -getPillar(modalDims?.width || 0),
                  left: -getPillar(modalDims?.width || 0),
                  pointerEvents: 'none',
                  objectFit: 'cover',
                  zIndex: 1,
                }}
              />}
            </Box>
            <Flex my="50px" sx={{ gap: "20px" }} flexDirection={['column', 'row']} justifyContent="center" alignItems="center">

              {doesMediaExist &&
                <Flex flex={1} alignItems="center" justifyContent="center"
                  sx={{ cursor: 'pointer', width: 'fit-content', height: 'fit-content' }}
                  onClick={() => setCropVideo(true)}
                >
                  <Text as={IoCrop} color="#9B59B6" size={24} />
                  <Text fontFamily="Roboto" ml={1} fontSize={16} lineHeight="24px" color="#9B59B6" >Redo Crop</Text>
                </Flex>
              }

              <Button bg="transparent" sx={{ border: 0 }} {...getRootProps()} >
                <VideoUploadLabel htmlFor="selfie_video" width="100%" >
                  <input
                    {...getInputProps()}
                    accept="image/*"
                  />
                  <Flex alignItems="center" justifyContent="center" >
                    <Text as={GoFileMedia} color="#9B59B6" size={24} />
                    <Text fontFamily="Roboto" fontSize={16} ml={1} lineHeight="24px" color="#9B59B6" >Replace Photo</Text>
                  </Flex>
                </VideoUploadLabel>
              </Button>

              <Button
                onClick={onClose}
                width="100px"
                height={"40px"}
                p="0 20px"
                sx={{ borderRadius: '23px', background: '#BDBDBD', border: 0 }}
              >
                Cancel
              </Button>
            </Flex>
          </Flex>
          :
          <MediaUploadSection
            type="Photo"
            isForEdit={!!mediaDetails?.['hls-portrait']}
            UploadButton={
              <Button
                {...getRootProps()}
                display="flex"
                fontWeight="normal"
                textAlign="center"
                sx={{ borderRadius: '99px', border: '0px solid rgba(0, 0, 0, 0.20)' }}
              >
                <VideoUploadLabel htmlFor="selfie_video" width="100%" >
                  <input
                    {...getInputProps()}
                    accept="image/*"
                  />
                  <Flex flex={1} alignItems="center" justifyContent="center" gap={2}>
                    <Text m="auto" as={MdOutlineFileUpload} color="white" size={24} />
                    <Text fontFamily="Poppins" ml="4px" fontSize={16} lineHeight="24px" color="white" >Upload Photo</Text>
                  </Flex>
                </VideoUploadLabel>
              </Button>
            }
            onClose={onClose}
          />}
    </Modal>
    }
    {(imageUrl) ? (
      <Box
        width={["122px", "153px"]}
        height={["149.64px", "187.65px"]}
        id={framleLayerDetails?.width + "  " + framleLayerDetails?.height}
        ref={(el) => {
          if (!el) return;
          const { width, height } = el.getBoundingClientRect();
          // const frameAR = framleLayerDetails?.height && framleLayerDetails?.width
          //   ? framleLayerDetails.height / framleLayerDetails.width
          //   : mediaDetailsClips?.height && mediaDetailsClips?.width
          //     ? mediaDetailsClips.height / mediaDetailsClips.width
          //     : null;
          // const height = frameAR ? Math.round(width * frameAR) : el.getBoundingClientRect().height;
          setThumbnailDims(prev => (prev?.width === width && prev?.height === height ? prev : { width, height }));
        }}
        sx={{ borderRadius: '11.559px', overflow: 'hidden', position: 'relative', bg: 'black' }}
        onClick={() => setMediaUpload(true)}
      >
        <ReactAvatarEditor
          image={localPreview || newImageObj}
          height={editorDims.height}
          width={editorDims.width}
          color={[250, 250, 250, 0]}
          id={`${newImageObj}  scale:${mediaDetailsClips?.zoom + "  " + (+scalePosition(mediaDetailsClips?.zoom) * 3) / 100}  position:${JSON.stringify(renderPositionForVGBImage({
            x: mediaDetailsClips?.left,
            y: mediaDetailsClips?.top,
          }), null, 2)}`}
          scale={mediaDetailsClips?.zoom === 0 ? 1 : (+scalePosition(mediaDetailsClips?.zoom) * 3) / 100}
          position={renderPositionForVGBImage({
            x: mediaDetailsClips?.left,
            y: mediaDetailsClips?.top,
          })}
          crossOrigin="anonymous"
          style={{
            // display: 'block',
            minWidth: "100%",
            width: "100%",
            height: "100%",
            objectFit: 'cover',
            // zoom: zoom,
          }}
        />
        {/* Bridge overlay: show the just-cropped local image until the real image has
            actually loaded into the editor (onLoadSuccess above), so a just-processed
            upload never flashes the black tile background while newImageObj is fetched. */}
        {localPreview && <Image
          src={localPreview}
          sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 1 }}
        />}
        {frameSrc && <Image
          // height={"100%"}
          width={(thumbnailDims?.width || 0) + (getPillar(thumbnailDims?.width || 0) * 2)}
          maxWidth='inherit'
          src={frameSrc}
          sx={{
            position: 'absolute',
            top: -getPillar(thumbnailDims?.width || 0),
            left: -getPillar(thumbnailDims?.width || 0),
            pointerEvents: 'none',
            objectFit: 'cover',
            zIndex: 1,
          }}
        />}
        <Box mt="-45px" sx={{ position: 'relative', zIndex: 2 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="39" height="39" viewBox="0 0 39 39" fill="none">
            <path d="M0.436523 0.982422H26.5827C32.9697 0.982422 38.1473 6.16009 38.1473 12.5471V38.6932H12.0012C5.61419 38.6932 0.436523 33.5156 0.436523 27.1286V0.982422Z" fill="white" fill-opacity="0.7" />
            <path d="M29.0723 12.81L26.7943 10.4881C26.7069 10.399 26.6031 10.3283 26.4889 10.2801C26.3747 10.2319 26.2523 10.207 26.1287 10.207C26.0051 10.207 25.8827 10.2319 25.7685 10.2801C25.6543 10.3283 25.5505 10.399 25.4631 10.4881L21.1516 14.8826C20.975 15.0625 20.8758 15.3065 20.8757 15.5609V17.8828C20.8758 18.1373 20.9749 18.3813 21.1515 18.5613C21.328 18.7412 21.5675 18.8423 21.8171 18.8423H24.0957C24.3453 18.8422 24.5847 18.7411 24.7612 18.5612L29.0723 14.1667C29.1597 14.0776 29.2291 13.9719 29.2764 13.8555C29.3237 13.7391 29.3481 13.6143 29.3481 13.4884C29.3481 13.3624 29.3237 13.2376 29.2764 13.1212C29.2291 13.0048 29.1597 12.8991 29.0723 12.81ZM23.7059 16.9234H22.7585V15.9583L26.1287 12.5233L27.0756 13.4884L23.7059 16.9234ZM26.496 19.7652C26.2463 19.7652 26.0069 19.8663 25.8303 20.0463C25.6538 20.2262 25.5546 20.4702 25.5546 20.7247V23.0194L24.1609 21.5989C23.6702 21.0998 23.0053 20.8194 22.312 20.8194C21.6187 20.8194 20.9537 21.0998 20.463 21.5989L19.8059 22.2686L17.4656 19.8833C16.9755 19.3844 16.3111 19.1043 15.6185 19.1043C14.9259 19.1043 14.2615 19.3844 13.7714 19.8833L12.3754 21.3062V15.9273C12.3757 15.673 12.475 15.4291 12.6515 15.2493C12.828 15.0694 13.0672 14.9682 13.3168 14.9679H18.965C19.2147 14.9679 19.4541 14.8668 19.6307 14.6868C19.8072 14.5069 19.9064 14.2629 19.9064 14.0084C19.9064 13.7539 19.8072 13.5099 19.6307 13.3299C19.4541 13.15 19.2147 13.0489 18.965 13.0489H13.3168C12.568 13.0497 11.8502 13.3533 11.3207 13.8929C10.7913 14.4325 10.4935 15.1642 10.4927 15.9273V27.441C10.4935 28.2042 10.7913 28.9358 11.3207 29.4755C11.8502 30.0151 12.568 30.3186 13.3168 30.3195H24.6132C25.362 30.3186 26.0798 30.0151 26.6093 29.4755C27.1387 28.9358 27.4365 28.2042 27.4373 27.441V20.7247C27.4373 20.4702 27.3382 20.2262 27.1616 20.0463C26.9851 19.8663 26.7456 19.7652 26.496 19.7652ZM13.3168 28.4005C13.0672 28.4002 12.828 28.299 12.6515 28.1191C12.475 27.9393 12.3757 27.6954 12.3754 27.441V24.0196L15.1026 21.24C15.2422 21.106 15.4267 21.0313 15.6185 21.0313C15.8103 21.0313 15.9949 21.106 16.1345 21.24L19.1167 24.2788C19.1247 24.2876 19.1277 24.2997 19.136 24.3082L23.1514 28.4005H13.3168ZM25.5546 27.441C25.5499 27.626 25.4913 27.8054 25.3863 27.9563L21.1371 23.6254L21.7942 22.9557C21.9317 22.8161 22.1179 22.7378 22.312 22.7378C22.506 22.7378 22.6922 22.8161 22.8298 22.9557L25.5546 25.7329V27.441Z" fill="#9B59B6" />
          </svg>
        </Box>
      </Box>
    ) : (
      <Box width={["122px", "153px"]} height={["149.661px", "188.128px"]}>
        <Flex
          fontWeight="normal"
          fontSize={2}
          flex={1}
          height="100%"
          justifyContent="center"
          flexDirection="column"
          alignItems={'center'}
          textAlign="center"
          p={3}
          bg="#F3FCFF"
          color="#64748B"
          sx={{ borderRadius: 12, cursor: 'pointer', border: '1px dashed #2D9CDB' }}
          onClick={() => setMediaUpload(true)}
        >
          <Text as={GoFileMedia} color="#64748B" size={24} />
          <Text fontFamily="Poppins" fontSize={16} lineHeight="24px" color="#64748B" >Photo</Text>
        </Flex>
        {/* <NewStoryWithMediumButton
        type="images"
        disabled={isLoading}
        handleChange={handleChange}
        label="Photo"
      >
        <GoFileMedia color="#64748B" size={24} />
      </NewStoryWithMediumButton> */}
      </Box>
    )}
  </Box>

};

ImageRenderer.propTypes = {
  image: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
};

export default ImageRenderer;
